import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Shield, Zap, Users, MapPin, Truck } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const HomePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-20 pb-24 overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=1600&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.3)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/80 via-blue-800/70 to-blue-900/80 z-0" />

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Truck className="w-4 h-4" />
              Built for Field Service Professionals
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6 drop-shadow-lg">
              Run Your Service Business<br />
              <span className="text-green-400">Like a Pro</span>
            </h1>
            <p className="text-lg md:text-xl mb-8 max-w-3xl mx-auto text-blue-100">
              Route optimization, job tracking, invoicing, and business management—all in one place. Built for businesses that go to the customer.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link 
                to="/register" 
                className="bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-green-700 shadow-lg transition-colors inline-flex items-center justify-center gap-2"
              >
                Start Free 14-Day Trial
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link 
                to="/pricing"
                className="bg-white/10 backdrop-blur text-white border border-white/30 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white/20 transition-colors"
              >
                See Pricing
              </Link>
            </div>
            <p className="mt-4 text-sm text-blue-200">
              No credit card required • Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* Quick Value Props */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="p-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Zap className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Simple Setup</h3>
              <p className="text-sm text-gray-600">Up and running in minutes</p>
            </div>
            <div className="p-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <MapPin className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Smart Routing</h3>
              <p className="text-sm text-gray-600">Save time and fuel</p>
            </div>
            <div className="p-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Team Ready</h3>
              <p className="text-sm text-gray-600">No per-user fees</p>
            </div>
            <div className="p-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Shield className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Tax Ready</h3>
              <p className="text-sm text-gray-600">Schedule C reports built-in</p>
            </div>
          </div>
        </div>
      </section>

      {/* Plans Overview */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              Start Solo. Scale to a Team.
            </h2>
            <p className="text-lg text-gray-600">
              Choose the plan that fits today—upgrade anytime
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Solo */}
            <div className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-blue-400 hover:shadow-lg transition-all">
              <div className="text-center mb-6">
                <span className="inline-block bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold mb-3">
                  SOLO
                </span>
                <div className="text-4xl font-bold text-gray-900">$19<span className="text-lg font-normal text-gray-500">/mo</span></div>
                <p className="text-gray-600 mt-2">Perfect for getting started</p>
              </div>
              
              <ul className="space-y-3 mb-6">
                {[
                  'Unlimited customers & jobs',
                  'Invoicing & online payments',
                  'Expense tracking',
                  'Receipt scanner',
                  'Schedule C tax reports'
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Link 
                to="/register" 
                className="block w-full text-center py-3 border-2 border-blue-600 text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Start Free Trial
              </Link>
            </div>

            {/* Pro */}
            <div className="bg-gradient-to-b from-green-50 to-white rounded-xl border-2 border-green-400 p-6 shadow-lg relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-green-600 text-white px-4 py-1 rounded-full text-sm font-bold">
                  MOST POPULAR
                </span>
              </div>
              
              <div className="text-center mb-6 mt-2">
                <span className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold mb-3">
                  PRO
                </span>
                <div className="text-4xl font-bold text-gray-900">$49<span className="text-lg font-normal text-gray-500">/mo</span></div>
                <p className="text-gray-600 mt-2">For growing teams</p>
              </div>
              
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-sm">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700"><strong>Everything in Solo, plus:</strong></span>
                </li>
                {[
                  'Up to 15 team members',
                  'Route optimization',
                  'Automated email reminders',
                  'Customer portal',
                  'P&L reports'
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Link 
                to="/register" 
                className="block w-full text-center py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
              >
                Start Free Trial
              </Link>
            </div>

            {/* Enterprise */}
            <div className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-purple-400 hover:shadow-lg transition-all">
              <div className="text-center mb-6">
                <span className="inline-block bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-semibold mb-3">
                  ENTERPRISE
                </span>
                <div className="text-4xl font-bold text-gray-900">$129<span className="text-lg font-normal text-gray-500">/mo</span></div>
                <p className="text-gray-600 mt-2">For serious operations</p>
              </div>
              
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-sm">
                  <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-gray-700"><strong>Everything in Pro, plus:</strong></span>
                </li>
                {[
                  'Unlimited team members',
                  'Custom branding',
                  'Marketing automation',
                  'API access',
                  'Multi-location support'
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm">
                    <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Link 
                to="/register" 
                className="block w-full text-center py-3 border-2 border-purple-600 text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition-colors"
              >
                Start Free Trial
              </Link>
            </div>
          </div>

          <p className="text-center text-gray-500 mt-8 text-sm">
            All plans include a 14-day free trial. No credit card required.
          </p>
        </div>
      </section>

      {/* Core Features */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              Everything You Need to Run Your Business
            </h2>
            <p className="text-lg text-gray-600">
              Purpose-built for field service professionals
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: '👥',
                title: 'Customer Management',
                description: 'Keep detailed records, track service history, and never lose a customer\'s information.'
              },
              {
                icon: '📋',
                title: 'Job Tracking',
                description: 'From quote to completion—track every job\'s status and never miss a follow-up.'
              },
              {
                icon: '💰',
                title: 'Invoicing & Payments',
                description: 'Create professional invoices and get paid faster with online payment processing.'
              },
              {
                icon: '🗺️',
                title: 'Route Optimization',
                description: 'Smart routing sequences your jobs for maximum efficiency and minimum drive time.'
              },
              {
                icon: '🏠',
                title: 'Property Tracking',
                description: 'Store property details, measurements, gate codes, and service history.'
              },
              {
                icon: '📊',
                title: 'Business Insights',
                description: 'Track revenue, monitor expenses, and see performance at a glance.'
              }
            ].map((feature) => (
              <div key={feature.title} className="bg-gray-50 p-6 rounded-xl hover:shadow-md transition-shadow">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold mb-2 text-gray-900">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Field Service Industries */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              Built for Field Service Professionals
            </h2>
            <p className="text-lg text-gray-600">
              If you drive to your customers, SuddyFlow is built for you
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: '💦', name: 'Pressure Washing' },
              { icon: '🪟', name: 'Window Cleaning' },
              { icon: '🌳', name: 'Landscaping' },
              { icon: '🧹', name: 'House Cleaning' },
              { icon: '🏊', name: 'Pool Service' },
              { icon: '❄️', name: 'HVAC' },
              { icon: '🔧', name: 'Plumbing' },
              { icon: '⚡', name: 'Electrical' },
              { icon: '🛠️', name: 'Handyman' },
              { icon: '🐛', name: 'Pest Control' },
              { icon: '🎄', name: 'Christmas Lights' },
              { icon: '🌲', name: 'Tree Service' }
            ].map((industry) => (
              <div 
                key={industry.name}
                className="bg-white p-4 rounded-lg border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all text-center"
              >
                <div className="text-3xl mb-2">{industry.icon}</div>
                <p className="font-medium text-gray-800 text-sm">{industry.name}</p>
              </div>
            ))}
          </div>

          <p className="text-center mt-8 text-gray-500">
            And many more field service businesses
          </p>
        </div>
      </section>

      {/* Security & Trust */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Enterprise-Grade Security
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Your customer data, financial information, and business details are protected with bank-level security. Built with over 20 years of enterprise software development expertise.
              </p>
              <ul className="space-y-3">
                {[
                  'Encrypted data transmission (SSL/TLS)',
                  'Secure payment processing via Stripe',
                  'Regular security updates and monitoring',
                  'Role-based access control',
                  'Your data is never sold or shared'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-8 rounded-2xl">
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Your Data, Protected</h3>
                <p className="text-gray-600">
                  We take security seriously. Your business information is encrypted, backed up, and protected at every level.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              Powerful Integrations
            </h2>
            <p className="text-lg text-gray-600">
              Connect SuddyFlow with the tools you already use
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: '📊',
                title: 'QuickBooks Sync',
                description: 'Automatically sync invoices, payments, and expenses.'
              },
              {
                icon: '💳',
                title: 'Online Payments',
                description: 'Accept credit cards and get paid faster.'
              },
              {
                icon: '🌐',
                title: 'Customer Portal',
                description: 'Let customers view jobs and pay online.'
              },
              {
                icon: '🧮',
                title: 'Tax Reports',
                description: 'Schedule C reports and quarterly estimates.'
              }
            ].map((integration) => (
              <div key={integration.title} className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
                <div className="text-4xl mb-3">{integration.icon}</div>
                <h3 className="font-bold text-gray-900 mb-2">{integration.title}</h3>
                <p className="text-sm text-gray-600">{integration.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1552664730-d307ca884978?w=1600&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.3)'
          }}
        />
        <div className="absolute inset-0 bg-blue-900/80 z-0" />

        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Streamline Your Business?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Join field service professionals who are growing faster with SuddyFlow.
          </p>
          <Link 
            to="/register" 
            className="inline-flex items-center gap-2 bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-green-700 shadow-lg transition-colors"
          >
            Start Your Free Trial
            <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="mt-6 text-blue-200 text-sm">
            14 days free • No credit card required • Cancel anytime
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;